import { IContentControlStudioViewModel } from 'app/js/src/domain/content-control/studio';
import { IContentControlManager } from 'app/js/src/domain/content-control/manager';
import { IContentControlVideoSource } from 'app/js/src/domain/content-control/i-content-control-video-source';
import { IContentControlStudioTimeline } from "../content-control-studio-timeline/i-content-control-studio-timeline";
import { StudioPlaybackController } from "./studio-playback-controller";

export abstract class StudioAudioManager {
  audioCtx: AudioContext;
  audioOptions: string[] = [];
  subtitleOptions: string[] = [];
  selectedSubtitleOptions: string[] = [IContentControlStudioViewModel.SK];
  setupAudioFunctionHandler: () => void;

  constructor(
    protected readonly getVideo: () => IContentControlVideoSource,
    protected readonly _videoContainer: HTMLDivElement,
    protected readonly _manager: IContentControlManager,
    protected readonly _timeline: IContentControlStudioTimeline,
    protected readonly _playback: StudioPlaybackController,
  ) {
    this.setupAudioFunctionHandler = this.initAudioContext.bind(this);
  }

  protected abstract setupAudioContextImpl(
    audioCtx: AudioContext,
    gain: GainNode,
    videoContainer: HTMLDivElement,
    availableTracks: { [lang: string]: MediaElementAudioSourceNode },
  ): void;

  protected abstract handleAudioOptionChangeImpl(
    option: string,
    availableTracks: { [lang: string]: MediaElementAudioSourceNode },
    video: IContentControlVideoSource,
    isVideoAudioTrack: boolean,
    syncAudioFn: () => void,
  ): void;

  initAudioTracks(): void {
    this.audioOptions.unshift(IContentControlStudioViewModel.VK);
    for (const audio of this._manager.audioFiles) {
      this.audioOptions.push(audio.language);
      const audioSource = new Audio();
      audioSource.src = audio.url;
      audioSource.lang = audio.language;
      audioSource.crossOrigin = "anonymous";
      this._videoContainer.appendChild(audioSource);
    }
  }

  initAudioContext(): void {
    if (!this.audioCtx) {
      this.audioCtx = new AudioContext();
      const gain = this.audioCtx.createGain();
      this._manager.setupAudio(this.audioCtx, gain);
      gain.gain.value = 1;
      this.setupAudioContextImpl(
        this.audioCtx,
        gain,
        this._videoContainer,
        this._playback.availableAudioTracks,
      );
      gain.connect(this.audioCtx.destination);
      document.removeEventListener("click", this.setupAudioFunctionHandler);
    }
  }

  onAudioChange(option: string): void {
    if (this.audioOptions.find((x) => x === option)) {
      this._playback.selectedAudioOption = option;
      this.handleAudioOptionChangeImpl(
        option,
        this._playback.availableAudioTracks,
        this.getVideo(),
        this._playback.isVideoAudioTrack,
        this.syncAudio.bind(this),
      );
    }
  }

  syncAudio(): void {
    if (IContentControlStudioViewModel.VK != this._playback.selectedAudioOption) {
      const cat = this._playback.availableAudioTracks[this._playback.selectedAudioOption];
      cat.mediaElement.currentTime = this._timeline.currentVideoPlayTimeSecs();
      if (this._playback.isVideoPaused) {
        cat.mediaElement.pause();
      } else {
        cat.mediaElement.play().catch(console.error);
      }
    }
  }

  initSubtitleTracks(): void {
    this.subtitleOptions.unshift(IContentControlStudioViewModel.SK);
    this.subtitleOptions = this._manager.subtitleFiles.map((x) => {
      this._manager.loadSubtitle(x.language, x.url, x.fps);
      return x.language;
    });
  }

  onSubtitleChange(options: string[]): void {
    if (options && options.length == 0) {
      this.selectedSubtitleOptions = [IContentControlStudioViewModel.SK];
    } else {
      this.selectedSubtitleOptions = [...options];
    }
  }
}
